import React, { useEffect, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";
import apiClient from "../lib/api";
import { ProjectPdfPreview } from "./pdf/ProjectPdfPreview";

// 讨论附件全屏预览: 图片/PDF 页内打开, 左右箭头/键盘/触摸滑动切换
export default function AttachmentLightbox({ items, index, onClose, onIndex, urlOf, zh = true, api = apiClient }) {
  const touchX = useRef(null);
  const many = items.length > 1;
  const prev = () => onIndex((index - 1 + items.length) % items.length);
  const next = () => onIndex((index + 1) % items.length);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowLeft" && many) { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight" && many) { e.preventDefault(); next(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const cur = items[index];
  if (!cur) return null;
  const ct = cur.contentType || "";
  const url = urlOf(cur.fileId);
  const isImg = ct.startsWith("image/");
  const isPdf = ct === "application/pdf" || (cur.filename || "").toLowerCase().endsWith(".pdf");
  return (
    <div data-testid="attachment-lightbox" className="fixed inset-0 z-[9990] bg-slate-950/90 backdrop-blur-sm flex flex-col" onClick={onClose}>
      <div className="mobile-safe-topbar flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 text-white shrink-0" onClick={(e) => e.stopPropagation()}>
        <p className="flex-1 basis-32 text-sm font-semibold truncate min-w-0" data-testid="lightbox-filename" title={cur.filename}>{cur.filename}</p>
        <div className="flex items-center gap-2 shrink-0">
          {many && <span className="text-[11px] text-slate-300 tabular-nums" data-testid="lightbox-counter">{index + 1} / {items.length}</span>}
          <a href={url} download={cur.filename} data-testid="lightbox-download" onClick={(e) => e.stopPropagation()}
            className="inline-flex min-h-11 items-center gap-1 rounded-full bg-slate-800 hover:bg-slate-700 px-3 py-1.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
            <Download className="h-3.5 w-3.5" />{zh ? "下载" : "Download"}
          </a>
          <button type="button" data-testid="lightbox-close" onClick={onClose}
            className="inline-flex min-h-11 items-center gap-1 rounded-full bg-slate-800 hover:bg-slate-700 px-3 py-1.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
            <X className="h-3.5 w-3.5" />{zh ? "关闭" : "Close"}
          </button>
        </div>
      </div>
      {/* 单张凭证不预留翻页栏；小图按可用宽高等比放大，避免支票只占屏幕中间一小块。 */}
      <div className={`flex-1 min-h-0 relative flex items-center justify-center pb-2 ${many ? "px-12 sm:px-14" : "px-2"}`}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current === null || !many) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(dx) > 40) (dx > 0 ? prev() : next());
          touchX.current = null;
        }}>
        {many && (
          <button type="button" data-testid="lightbox-prev" aria-label={zh ? "上一份附件" : "Previous attachment"} onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <div className="max-h-full max-w-full w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          {isImg ? (
            <img src={url} alt={cur.filename} data-testid="lightbox-image" className="h-full w-full min-h-0 min-w-0 object-contain rounded-lg select-none" draggable={false} />
          ) : isPdf ? (
            <div data-testid="lightbox-pdf" className="w-full h-full min-h-0 min-w-0">
              {/* 支票 PDF 默认逐页适宽放大，原件下载保持调用方原地址，不依赖浏览器插件。 */}
              <ProjectPdfPreview key={cur.fileId} api={api} file={cur} zh={zh} allowBrowserFallback={false} showThumbnails={false} downloadUrl={url} />
            </div>
          ) : (
            <div data-testid="lightbox-file" className="rounded-2xl bg-slate-900 border border-slate-700 px-10 py-8 text-center text-white max-w-sm">
              <FileText className="h-10 w-10 mx-auto text-slate-400" />
              <p className="text-sm font-semibold mt-3 break-all">{cur.filename}</p>
              <p className="text-[11px] text-slate-400 mt-1">{zh ? "该文件类型不支持页内预览" : "Preview not available for this file type"}</p>
              <a href={url} download={cur.filename} className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-indigo-600 hover:bg-indigo-500 px-4 py-2 text-xs font-semibold">
                <Download className="h-3.5 w-3.5" />{zh ? "下载文件" : "Download file"}
              </a>
            </div>
          )}
        </div>
        {many && (
          <button type="button" data-testid="lightbox-next" aria-label={zh ? "下一份附件" : "Next attachment"} onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
}
